// Client Statement of Account (SOA) — jsPDF generator.
// Matches the look-and-feel used by invoice PDFs in Invoices.tsx.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface SoaEntry {
  date: string;
  kind: string;
  ref: string;
  particulars: string;
  debit: number;
  credit: number;
  balance?: number;
}

export interface SoaData {
  client: {
    clientCode: string; clientName: string; legalName?: string | null;
    pan?: string | null; gstin?: string | null; address?: string | null; phone?: string | null;
  };
  period: { from: string | null; to: string | null };
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  entries: SoaEntry[];
}

export interface FirmInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  pan?: string;
}

// dd-mmm-yy per project formatting standard
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en', { month: 'short' });
  const yr = String(d.getFullYear()).slice(-2);
  return `${day}-${mon}-${yr}`;
};

// PDF amounts: include decimals per project standard
const fmtAmt = (n: number | null | undefined): string => {
  if (n == null || n === 0) return '';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtBal = (n: number): string => {
  const abs = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `${abs} Cr` : abs;
};

export function generateSoaPdf(data: SoaData, firm: FirmInfo): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 14; // margin
  let y = M;

  // ── Firm header ─────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(firm.name || 'Statement of Account', M, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  if (firm.address) {
    const lines = doc.splitTextToSize(firm.address, pageW - 2 * M);
    doc.text(lines, M, y);
    y += lines.length * 4;
  }
  const fLine = [
    firm.phone && `Phone: ${firm.phone}`,
    firm.email && `Email: ${firm.email}`,
    firm.gstin && `GSTIN: ${firm.gstin}`,
    firm.pan && `PAN: ${firm.pan}`,
  ].filter(Boolean).join('   ');
  if (fLine) { doc.text(fLine, M, y); y += 5; }

  doc.setDrawColor(180);
  doc.line(M, y, pageW - M, y);
  y += 6;

  // ── Title ─────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Statement of Account', pageW / 2, y, { align: 'center' });
  y += 6;

  // ── Period band ───────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const periodTxt = data.period.from || data.period.to
    ? `Period: ${fmtDate(data.period.from) || '—'}  to  ${fmtDate(data.period.to) || fmtDate(new Date().toISOString())}`
    : `As on: ${fmtDate(new Date().toISOString())}`;
  doc.text(periodTxt, pageW / 2, y, { align: 'center' });
  y += 6;

  // ── Bill-to block ─────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Account of:', M, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`${data.client.clientName} (${data.client.clientCode})`, M, y);
  y += 5;
  if (data.client.legalName && data.client.legalName !== data.client.clientName) {
    doc.setFontSize(9);
    doc.text(data.client.legalName, M, y); y += 4;
  }
  doc.setFontSize(9);
  if (data.client.address) {
    const lines = doc.splitTextToSize(data.client.address, (pageW - 2 * M) * 0.65);
    doc.text(lines, M, y);
    y += lines.length * 4;
  }
  const cMeta = [
    data.client.gstin && `GSTIN: ${data.client.gstin}`,
    data.client.pan && `PAN: ${data.client.pan}`,
    data.client.phone && `Phone: ${data.client.phone}`,
  ].filter(Boolean).join('   ');
  if (cMeta) { doc.text(cMeta, M, y); y += 5; }
  y += 2;

  // ── Ledger table ─────────────────────────────────────────
  const rows = data.entries.map((e) => [
    fmtDate(e.date),
    e.ref || '—',
    e.particulars,
    fmtAmt(e.debit),
    fmtAmt(e.credit),
    e.balance != null ? fmtBal(e.balance) : '',
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Ref #', 'Particulars', 'Debit (₹)', 'Credit (₹)', 'Balance (₹)']],
    body: rows,
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 1.6, lineColor: [200, 200, 200] },
    headStyles: { fillColor: [40, 60, 90], textColor: 255, halign: 'left', fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 25 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },
    foot: [[
      '', '', 'Total',
      fmtAmt(data.totalDebit),
      fmtAmt(data.totalCredit),
      fmtBal(data.closingBalance),
    ]],
    footStyles: { fillColor: [240, 240, 245], textColor: 20, fontStyle: 'bold' },
  });

  // ── Closing summary ──────────────────────────────────────
  const finalY = (doc as any).lastAutoTable.finalY + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const closeTxt = data.closingBalance >= 0
    ? `Balance Receivable: ₹${fmtBal(data.closingBalance)}`
    : `Balance Payable (advance): ₹${fmtBal(data.closingBalance)}`;
  doc.text(closeTxt, pageW - M, finalY, { align: 'right' });

  // Footer on every page
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(120);
    const footer = `Generated on ${fmtDate(new Date().toISOString())}   •   Page ${i} of ${pageCount}   •   E. & O. E.`;
    doc.text(footer, pageW / 2, doc.internal.pageSize.getHeight() - 7, { align: 'center' });
    doc.setTextColor(0);
  }

  return doc;
}
